import React from "react"
import Typography from "aldoo-ra/Typography"
import GridSummary from "aldoo-ra/GridSummary"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"

const ReviewStep = ({
  formData,
  selectedPack,
  selectedModel,
  models = []
}) => {
  // Generate activation codes preview
  const generateCodesPreview = () => {
    if (formData.activationCodeType === "explicit") {
      const codes = formData.explicitCodes
        .split("\n")
        .filter((code) => code.trim())
      return codes.length > 0 ? codes[0] : "No codes entered"
    }

    // Use formData pattern first (from step 3 model selection), then fallback to selectedModel, then default
    const pattern = formData.codePattern || selectedModel?.codePattern || "XXXX-XXXX"
    const codeSet = formData.codeSet || selectedModel?.codeSet || "A-Z,0-9"
    
    // Parse character set to build available characters
    let chars = ""
    const sets = codeSet.split(",")
    sets.forEach((set) => {
      const trimmedSet = set.trim()
      if (trimmedSet === "A-Z") chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
      else if (trimmedSet === "0-9") chars += "0123456789"
      else if (trimmedSet === "a-z") chars += "abcdefghijklmnopqrstuvwxyz"
    })
    
    // Default to alphanumeric if no valid sets found
    if (!chars) chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    
    return pattern.replace(
      /X/g,
      () => chars[Math.floor(Math.random() * chars.length)]
    )
  }

  // Generate GridSummary configuration
  const generateSummaryConfig = () => {
    // Use the correct count based on activation type
    const licenseCount = formData.activationCodeType === "explicit" 
      ? formData.customCodesCount || 0 
      : parseInt(formData.count)
    const totalSeats = licenseCount * parseInt(formData.seats)

    return {
      layout: [
        {
          type: "darkHeader",
          sections: [
            {
              label: "Generating",
              value: licenseCount,
              suffix: "licenses",
              valueColor: "text-admin_text_inv",
            },
            {
              label: "Total Capacity",
              value: totalSeats.toLocaleString(),
              suffix: "seats",
              valueColor: "text-admin_success",
            },
          ],
          children: {
            cells: [
              {
                type: "text",
                label: "License Pack",
                value: selectedPack?.name || "Not selected",
                colSpan: 1,
              },
              {
                type: "text",
                label: "License Model",
                value: (() => {
                  // Check if codes are uploaded from file (explicit codes)
                  if (formData.activationCodeType === "explicit") {
                    return "Imported from File"
                  }

                  // Check if using a pre-configured model
                  if (formData.useModel) {
                    return selectedModel?.name || "None selected"
                  }

                  // In custom mode, check if a code generation model is selected
                  if (formData.codeGenerationModelId) {
                    const codeGenModel = models.find(m => m._id === formData.codeGenerationModelId)
                    return `Custom Settings (Code: ${codeGenModel?.name || 'Unknown Model'})`
                  }

                  return "Custom Settings"
                })(),
                colSpan: 1,
              },
              {
                type: "text",
                label: "Seats per License",
                value: formData.seats,
                colSpan: 1,
              },
              {
                type: "text",
                label: "Users per License",
                value:
                  formData.users_per_license && Number(formData.users_per_license) > 0
                    ? formData.users_per_license
                    : "Unlimited",
                colSpan: 1,
              },
              {
                type: "text",
                label: "Validity",
                value:
                  formData.validityType === "lifetime"
                    ? "Lifetime"
                    : `${formData.validity} days`,
                colSpan: 1,
              },
              {
                type: "text",
                label: "Product ID",
                value: formData.productId,
                valueColor: "font-mono",
                colSpan: 1,
                colStart: 1,
              },
              {
                type: "text",
                label: "Sample Activation Code",
                value: generateCodesPreview(),
                valueColor: "font-mono",
                colSpan: 1,
                colStart: 2,
              },
              {
                type: "custom",
                label: "Supported Platforms",
                value: (() => {
                  const platforms = formData.supportedPlatforms || []
                  if (platforms.length === 0 || platforms.includes("any")) {
                    return (
                      <div className="flex items-center gap-2 mt-2">
                        {renderPlatformIcon("any", "w-4 h-4 text-admin_text_secondary")}
                        <span className="text-admin_text">
                          All Platforms
                        </span>
                      </div>
                    )
                  }
                  return (
                    <div className="flex items-center gap-3 flex-wrap mt-2">
                      {platforms.map(platform => (
                        <div key={platform} className="flex items-center gap-1">
                          {renderPlatformIcon(platform, "w-4 h-4 text-admin_text_secondary")}
                          <span>
                            {platform === "ios" ? "iOS" :
                             platform === "android" ? "Android" :
                             platform === "windows" ? "Windows" :
                             platform === "linux" ? "Linux" :
                             platform === "macos" ? "macOS" :
                             platform === "web" ? "Web" :
                             platform.charAt(0).toUpperCase() + platform.slice(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                })(),
                colSpan: 2,
                colStart: 1,
              },
            ],
          },
        },
      ],
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Typography 
          component="h2"
          variant="admin_h4" 
          className="mb-2"
        >
          Review & Generate
        </Typography>

        <Typography variant="admin_txt1_dim">
          Review your settings and generate the licenses
        </Typography>
      </div>

      {/* Use GridSummary component with data-driven configuration */}
      <GridSummary variant="admin" config={generateSummaryConfig()} />
    </div>
  )
}

export default ReviewStep